import { Suspense, useEffect, useMemo, useState } from "react";
import { Link, useHistory, useLocation } from "react-router-dom";
import { isMobile } from "../../helpers/functions/isMobile";
import "./SideBar.css";
import { useSidebar } from "./SidebarContext";
import SidebarTour from "./SidebarTour";
import useScreenSize from "../../hooks/useScreenSize";
import TargetAchievementBanner from "../../features/auction/target-management/components/TargetAchievementBanner";

// Design tokens aligned with the auction dashboard revamp:
// indigo / emerald / violet / amber / rose / cyan / slate.
const themeColors = {
  blue: {
    primary: "#2563EB",
    secondary: "#EFF6FF",
    icon: "#2563EB",
  },
  green: {
    primary: "#059669",
    secondary: "#ECFDF5",
    icon: "#059669",
  },
  purple: {
    primary: "#7C3AED",
    secondary: "#F5F3FF",
    icon: "#7C3AED",
  },
  orange: {
    primary: "#D97706",
    secondary: "#FFFBEB",
    icon: "#D97706",
  },
  red: {
    primary: "#DC2626",
    secondary: "#FEF2F2",
    icon: "#DC2626",
  },
  cyan: {
    primary: "#0891B2",
    secondary: "#ECFEFF",
    icon: "#0891B2",
  },
  default: {
    primary: "#475569",
    secondary: "#F1F5F9",
    icon: "#475569",
  },
};

function SideBar({
  components,
  local_storage_key,
  menu: menuList,
  dashboardTitle,
  defaultTheme = themeColors["default"],
  basePath = "/dashboard",
  counts,
  bannerComponent,
}) {
  const [activeMainMenu, setActiveMainMenu] = useState(() => {
    const parts = window.location.pathname.split("/").filter(Boolean);
    return parts.length >= 2 ? parts[1] : "";
  });
  const [expandedSubMenu, setExpandedSubMenu] = useState([]);
  const [showMainMenu, setShowMainMenu] = useState(true);
  const [runTour, setRunTour] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [showSearchResults, setShowSearchResults] = useState(false);

  const screenWidth = useScreenSize();

  const showMobileSidebar = screenWidth < 992; // Bootstrap's lg breakpoint is 992px

  // const [title, setTitle] = useState("Meratractor - Dashboard");
  // const refreshToken = localStorage.getItem("refreshToken");
  // const user = jwtDecode(refreshToken);

  const { isSidebarOpen, closeSidebar, openSidebar } = useSidebar();
  const history = useHistory();
  const location = useLocation();

  const findTitle = (contentKey) => {
    let itemLabel = "";
    for (const menuItem of menuList) {
      if (menuItem.key === contentKey) {
        itemLabel = menuItem.label;
        break;
      }
      if (menuItem?.children) {
        for (const subMenu of menuItem.children) {
          if (subMenu.key === contentKey) {
            itemLabel = subMenu.label;
            break;
          }
          if (subMenu?.children) {
            for (const childItem of subMenu?.children) {
              if (childItem?.key === contentKey) {
                itemLabel = childItem.label;
                break;
              }
            }
          }
        }
      }
    }
    return itemLabel ? `${itemLabel}` : `MeraTractor | Dashboard`;
  };

  const handleMainMenuClick = (item) => {
    const { key } = item;
    const isActive = activeMainMenu === key;
    setActiveMainMenu(key);
    setShowMainMenu((prev) => (isActive ? !prev : true));

    // Navigate to the main menu route (first child)
    if (item) {
      if (components[key]) {
        const path = `${basePath}/${key}`;
        history.push(path);
        // setTitle(item.label || "Meratractor - Dashboard");
      } else {
        const firstChildKey = item.children?.[0]?.key;
        if (firstChildKey && components[firstChildKey]) {
          const path = `${basePath}/${key}/${firstChildKey}`;
          history.push(path);
          // setTitle(item.label || "Meratractor - Dashboard");
        } else {
          const path = `${basePath}/${key}/${item.children[0].children[0].key}`;
          setExpandedSubMenu((prev) => [...prev, firstChildKey]);
          history.push(path);
          // setTitle(item.label || "Meratractor - Dashboard");
        }
      }
    }
  };

  const handleSubMenuClick = (sub, hasChildren, event) => {
    // Prevent event bubbling to avoid double triggers
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    if (hasChildren) {
      const { key } = sub;

      setExpandedSubMenu((prev) =>
        prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key],
      );
    } else {
      // This is a leaf menu item - navigate directly
      closeCanvas();
      const path = `${basePath}/${activeMainMenu}/${sub.key}`;

      // Check if we're already on this path to prevent unnecessary navigation
      if (location.pathname !== path) {
        history.push(path);
        // setTitle(sub.label);
      }
    }
  };

  const handleChildMenuClick = (child, event) => {
    // Prevent event bubbling
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    closeCanvas();
    const path = `${basePath}/${activeMainMenu}/${child.key}`;

    // Check if we're already on this path to prevent unnecessary navigation
    if (location.pathname !== path) {
      history.push(path);
      // setTitle(child.label);
    }
  };

  // Determine which component to render based on the current route
  const getCurrentComponentKey = () => {
    const currentPath = location.pathname;
    const relevantPath = currentPath.split("/").filter(Boolean);

    let componentKey = "";
    if (currentPath === basePath || currentPath === basePath + "/") {
      componentKey = menuList[0] && menuList[0].key;
      if (!components[componentKey]) {
        componentKey = menuList[0]?.children?.[0]?.key;
      }
    }
    if (relevantPath.length === 2) {
      componentKey = relevantPath[1];
      if (componentKey.includes(":")) {
        componentKey = componentKey?.split(":")[0];
      }
    }
    if (relevantPath.length > 2) {
      componentKey = relevantPath[relevantPath.length - 1];
      if (componentKey.includes(":")) {
        componentKey = componentKey?.split(":")[0];
      }
    }
    console.log("Current Component Key:", componentKey);
    return componentKey;
  };

  function NotFoundComponent() {
    return (
      <div className=" text-center fw-semibold mt-2 text-danger">
        Page Not Found....
      </div>
    );
  }

  const currentComponentKey = useMemo(
    () => getCurrentComponentKey(),
    [location],
  );
  const ActiveContentComponent =
    currentComponentKey && components[currentComponentKey]
      ? components[currentComponentKey]
      : NotFoundComponent;

  const closeCanvas = () => {
    const offcanvasCloseButton = document.getElementById(
      "offcanvasCloseButton",
    );
    if (offcanvasCloseButton) {
      offcanvasCloseButton.click();
    }
    closeSidebar(); // Close the sidebar when canvas is closed
  };

  // Search functionality
  const handleSearch = (query) => {
    setSearchQuery(query);

    if (!query.trim()) {
      setSearchResults([]);
      setShowSearchResults(false);
      return;
    }

    const results = [];
    const lowerQuery = query.toLowerCase();

    menuList.forEach((mainMenu) => {
      // Check main menu
      if (mainMenu.label.toLowerCase().includes(lowerQuery)) {
        results.push({
          label: mainMenu.label,
          path: components[mainMenu.key]
            ? `${basePath}/${mainMenu.key}`
            : mainMenu.children?.[0]?.key
              ? `${basePath}/${mainMenu.key}/${mainMenu.children[0].key}`
              : `${basePath}/${mainMenu.key}/${mainMenu.children[0].children[0].key}`,
          mainMenuKey: mainMenu.key,
          iconClass: mainMenu.iconClass,
          type: "main",
          theme: mainMenu.theme,
        });
      }

      // Check submenus
      mainMenu.children?.forEach((subMenu) => {
        if (subMenu.label.toLowerCase().includes(lowerQuery)) {
          results.push({
            label: `${mainMenu.label} > ${subMenu.label}`,
            path: subMenu.children
              ? `${basePath}/${mainMenu.key}/${subMenu.children[0].key}`
              : `${basePath}/${mainMenu.key}/${subMenu.key}`,
            mainMenuKey: mainMenu.key,
            subMenuKey: subMenu.key,
            iconClass: subMenu.iconClass,
            type: "submenu",
            theme: subMenu.theme || mainMenu.theme,
            hasChildren: !!subMenu.children,
          });
        }

        // Check child menus
        subMenu.children?.forEach((childMenu) => {
          if (childMenu.label.toLowerCase().includes(lowerQuery)) {
            results.push({
              label: `${mainMenu.label} > ${subMenu.label} > ${childMenu.label}`,
              path: `${basePath}/${mainMenu.key}/${childMenu.key}`,
              mainMenuKey: mainMenu.key,
              subMenuKey: subMenu.key,
              childMenuKey: childMenu.key,
              iconClass: childMenu.iconClass,
              type: "child",
              theme: childMenu.theme || subMenu.theme || mainMenu.theme,
            });
          }
        });
      });
    });

    setSearchResults(results);
    setShowSearchResults(results.length > 0);
  };

  const handleSearchResultClick = (result) => {
    // Set active states
    setActiveMainMenu(result.mainMenuKey);
    setShowMainMenu(true);

    // Expand parent submenus if needed
    if (result.subMenuKey && result.hasChildren) {
      setExpandedSubMenu((prev) =>
        prev.includes(result.subMenuKey) ? prev : [...prev, result.subMenuKey],
      );
    }

    // Clear search
    setSearchQuery("");
    setSearchResults([]);
    setShowSearchResults(false);

    // Close mobile canvas
    closeCanvas();
  };

  const SideBarMenu = () => {
    // Get color scheme for current active menu or use default
    const activeMenu = menuList.find((m) => m.key === activeMainMenu);
    const activeMenuColors = themeColors[activeMenu?.theme] || defaultTheme;
    // Top-level items with a directly-mapped component (no children) render full-width — no
    // submenu panel.
    const activeMenuHasChildren = !!activeMenu?.children?.length;

    return (
      <>
        <div
          className=" d-flex flex-column"
          style={{
            height: "100%",
            minHeight: "100%",
            overflow: "hidden",
            width: showMainMenu
              ? showMobileSidebar
                ? "100%"
                : "360px"
              : "80px",
            minWidth: showMainMenu
              ? showMobileSidebar
                ? "inherit"
                : "360px"
              : "80px",
            maxWidth: showMainMenu
              ? showMobileSidebar
                ? "100%"
                : "360px"
              : "80px",
          }}
        >
          {
            showMainMenu ? (
              <div
                style={{
                  width: "100%",
                  zIndex: 1002,
                }}
                className="position-relative"
              >
                <div className="bento-search-tile mb-2">
                  <div className="input-group input-group-sm">
                    <span className="input-group-text bg-transparent border-0 px-1">
                      <i
                        className="bi bi-search"
                        style={{ color: "#64748b" }}
                      ></i>
                    </span>
                    <input
                      type="search"
                      className="form-control border-0 bg-transparent shadow-none"
                      placeholder="Search menu..."
                      value={searchQuery}
                      onChange={(e) => handleSearch(e.target.value)}
                      autoFocus={!isMobile}
                    />
                  </div>
                </div>

                {showSearchResults && searchResults.length > 0 && (
                  <div
                    className="position-absolute bg-white border rounded-2 shadow-lg"
                    style={{
                      top: "100%",
                      left: 0,
                      right: 0,
                      maxHeight: "300px",
                      overflowY: "auto",
                      zIndex: 1003,
                    }}
                  >
                    <div className="list-group list-group-flush">
                      {searchResults.map((result, index) => {
                        const resultColors =
                          themeColors[result.theme] || defaultTheme;
                        return (
                          <Link
                            key={index}
                            className="list-group-item list-group-item-action d-flex align-items-start text-start text-decoration-none"
                            onClick={() => handleSearchResultClick(result)}
                            style={{
                              fontSize: "0.875rem",
                              borderLeft: `3px solid ${resultColors.primary}`,
                            }}
                            to={result.path}
                          >
                            {result.iconClass && (
                              <i
                                className={`${result.iconClass} me-2 mt-1`}
                                style={{ color: resultColors.primary }}
                              ></i>
                            )}
                            <div className="flex-grow-1">
                              <div
                                className="fw-semibold"
                                style={{ color: resultColors.primary }}
                              >
                                {result.label.split(" > ").pop()}
                              </div>
                              {result.type !== "main" && (
                                <small className="text-muted">
                                  {result.label
                                    .split(" > ")
                                    .slice(0, -1)
                                    .join(" > ")}
                                </small>
                              )}
                            </div>
                            <i
                              className="bi bi-arrow-right ms-2 mt-1"
                              style={{ color: resultColors.primary }}
                            ></i>
                          </Link>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            ) : null
            // <div className=" p-2 bg-glass border rounded-2 mb-2 text-center cursor-pointer">
            //   <div
            //     className="  d-flex flex-column align-items-center justify-content-center gap-1 menu-item"
            //     onClick={() => setShowMainMenu(true)}
            //   >
            //     <span className=" cursor-pointer">
            //       <i
            //         className="bi bi-search"
            //         style={{
            //           height: "1.5rem",
            //           width: "1.5rem",
            //           lineHeight: "1.5rem",
            //         }}
            //       ></i>
            //     </span>
            //     <small style={{ fontSize: "0.8rem" }}>Search</small>
            //   </div>
            // </div>
          }{" "}
          <div
            className=" d-flex flex-row flex-grow-1 bento-board"
            style={{ minHeight: 0 }}
          >
            {/* Layer 1: Icon Sidebar */}
            <div
              className="icon-sidebar bento-icon-rail d-flex flex-column align-items-center"
              style={{
                width: "80px",
                minWidth: "80px",
                position: "relative",
                zIndex: 1,
                overflowY: "auto",
              }}
            >
              <button
                type="button"
                className="bento-sidebar-toggle"
                aria-label={showMainMenu ? "Collapse menu" : "Expand menu"}
                title={showMainMenu ? "Collapse sidebar" : "Expand sidebar"}
                onClick={() => setShowMainMenu((v) => !v)}
              >
                <i
                  className={`bi ${showMainMenu ? "bi-layout-sidebar-inset" : "bi-layout-sidebar"}`}
                />
              </button>
              {menuList.map((item, index) => {
                const itemColors = themeColors[item.theme] || defaultTheme;
                const isActive = activeMainMenu === item.key;

                const linkURL = `${basePath}/${item.key}`;

                return (
                  <Link
                    className={`bento-icon-tile cursor-pointer ${
                      isActive ? "is-active" : ""
                    } ${item.className || ""} icon-sidebar-item-${index}`}
                    key={index}
                    onClick={(e) => {
                      if (!showMobileSidebar) {
                        // Desktop single-click: just switch active menu, don't navigate
                        e.preventDefault();
                        setActiveMainMenu(item.key);
                        setShowMainMenu(true);
                      } else {
                        handleMainMenuClick(item);
                      }
                    }}
                    onDoubleClick={(e) => {
                      if (!showMobileSidebar) {
                        // Desktop double-click: navigate to the menu
                        e.preventDefault();
                        handleMainMenuClick(item);
                      }
                    }}
                    title={
                      !showMobileSidebar
                        ? `${item.label} — Click to preview menu · Double-click to open`
                        : item.label
                    }
                    style={{
                      "--accent": itemColors.primary,
                      "--accent-soft": itemColors.secondary,
                      position: "relative",
                      zIndex: "auto",
                    }}
                    to={linkURL}
                  >
                    <span className="w-100 text-center">
                      <i
                        className={`${item.iconClass}`}
                        style={{
                          height: "1.5rem",
                          width: "1.5rem",
                          lineHeight: "1.5rem",
                          textAlign: "center",
                          fontSize: "1.1rem",
                        }}
                      ></i>
                    </span>
                    <small>{item?.label}</small>
                  </Link>
                );
              })}
            </div>
            {/* Layer 2: Submenu Sidebar */}
            <div
              className={`submenu-sidebar bento-submenu-panel flex-grow-1  ${
                !activeMenuHasChildren
                  ? "d-none"
                  : activeMainMenu && showMainMenu
                    ? "sidebar-open"
                    : "d-block d-lg-none"
              }`}
              style={{
                overflowY: "auto",
                minWidth: 0,
                flex: "1 1 auto",
                "--accent": activeMenuColors.primary,
                "--accent-soft": activeMenuColors.secondary,
                position: "relative",
                zIndex: "auto",
              }}
            >
              <div className="bento-submenu-header">
                <span className="bento-submenu-header-title">
                  {menuList.find((m) => m.key === activeMainMenu)?.label}
                </span>
                <button
                  type="button"
                  className="btn btn-sm bento-collapse-btn d-none d-lg-inline-flex"
                  onClick={() => setShowMainMenu(false)}
                  aria-label="Collapse submenu"
                >
                  <i className="bi bi-chevron-double-left"></i>
                </button>
              </div>

              {menuList
                .find((m) => m.key === activeMainMenu)
                ?.children?.map((sub, sub_index) => {
                  const hasChildren = !!sub.children;
                  const subMenuColors =
                    themeColors[sub.theme] || activeMenuColors;
                  const paths = location.pathname.split("/");
                  const isSubActive = paths[paths.length - 1] === sub.key;

                  const childKeys = hasChildren
                    ? sub?.children?.map((child) => child.key)
                    : [];

                  const leafCount =
                    !hasChildren && counts && counts[sub.key] > 0
                      ? counts[sub.key]
                      : 0;

                  // Leaf items show the exact count as a badge instead of this dot.
                  const isAnyCountActive = childKeys.some(
                    (key) => counts && counts[key] && counts[key] > 0,
                  );

                  const linkURL = hasChildren
                    ? `${basePath}/${activeMainMenu}/${sub?.children[0].key}`
                    : `${basePath}/${activeMainMenu}/${sub.key}`;

                  const isExpanded =
                    hasChildren && expandedSubMenu.includes(sub.key);
                  return (
                    <Link
                      key={sub.key}
                      className={`bento-submenu-tile submenu-sidebar-item-${sub_index} ${
                        isSubActive ? "is-active" : ""
                      } ${isExpanded ? "is-expanded" : ""}`}
                      style={{
                        "--accent": subMenuColors.primary,
                        "--accent-soft": subMenuColors.secondary,
                      }}
                      title={
                        leafCount > 0
                          ? `${sub.label} — ${leafCount} pending`
                          : undefined
                      }
                      to={linkURL}
                    >
                      {isAnyCountActive ? (
                        <span className="bento-count-dot"></span>
                      ) : null}

                      <div
                        className="bento-submenu-tile-row"
                        onClick={(event) => {
                          if (hasChildren || isMobile) {
                            handleSubMenuClick(sub, hasChildren, event);
                          }
                        }}
                      >
                        <span>
                          {sub.iconClass && (
                            <i className={`${sub.iconClass} me-2`}></i>
                          )}
                          {sub.label}
                        </span>
                        <span>
                          {leafCount > 0 && (
                            <span className="badge bg-danger ms-2">
                              {leafCount}
                            </span>
                          )}
                          {hasChildren && (
                            <i
                              className={`bi ${
                                isExpanded ? "bi-chevron-up" : "bi-chevron-down"
                              } ms-2`}
                            ></i>
                          )}
                        </span>
                      </div>
                      {isExpanded && (
                        <ul className="bento-child-list">
                          {sub.children.map((child) => {
                            const childColors =
                              themeColors[child.theme] || subMenuColors;
                            const pathParts = location.pathname.split("/");
                            const isChildActive =
                              pathParts[pathParts.length - 1] === child.key;
                            const linkURL = `${basePath}/${activeMainMenu}/${child.key}`;
                            return (
                              <Link
                                to={linkURL}
                                key={child.key}
                                className={`bento-child-tile ${
                                  isChildActive ? "is-active" : ""
                                }`}
                                style={{
                                  "--accent": childColors.primary,
                                  "--accent-soft": childColors.secondary,
                                }}
                                onClick={(event) => {
                                  if (isMobile) {
                                    handleChildMenuClick(child, event);
                                  }
                                }}
                              >
                                {child.iconClass && (
                                  <i className={`${child.iconClass} me-2`}></i>
                                )}
                                <span className="flex-grow-1">
                                  {child.label}
                                </span>

                                {counts &&
                                  counts[child.key] &&
                                  counts[child.key] > 0 && (
                                    <span className="badge ms-auto flex-shrink-0">
                                      {counts[child.key]}
                                    </span>
                                  )}
                              </Link>
                            );
                          })}
                        </ul>
                      )}
                    </Link>
                  );
                })}
            </div>
          </div>
        </div>
      </>
    );
  };

  useEffect(() => {
    document.title = findTitle(currentComponentKey);
  }, [location]);

  // Initialize state based on current route
  useEffect(() => {
    const currentPath = location.pathname;
    const relevantPath = currentPath.split("/").filter(Boolean);

    if (currentPath === basePath || currentPath === basePath + "/") {
      const firstMenu = menuList[0];
      const firstMenuKey = firstMenu?.key;
      if (components[firstMenuKey]) {
        setActiveMainMenu(firstMenu.key);
        setShowMainMenu(true);
        // setTitle(firstMenu.label || "Meratractor - Dashboard");
        history.replace(`${basePath}/${firstMenu.key}`);
        return;
      } else {
        const firstChild = firstMenu?.children?.[0];
        const firstChildKey = firstMenu?.children?.[0]?.key;
        if (firstChild && components[firstChildKey]) {
          setActiveMainMenu(firstChild.key);
          setShowMainMenu(true);
          // setTitle(firstChild.label || "Meratractor - Dashboard");
          history.replace(`${basePath}/${firstMenu.key}/${firstChildKey}`);
          return;
        } else {
          const secondChildKey = firstChild?.children?.[0]?.key;
          setActiveMainMenu(firstMenu.key);
          setShowMainMenu(true);
          setExpandedSubMenu((prev) => [...prev, firstChildKey]);
          // setTitle(firstChild.label || "Meratractor - Dashboard");
          history.replace(`${basePath}/${firstMenu.key}/${secondChildKey}`);
          return;
        }
      }
    }

    if (relevantPath.length === 2) {
      const menuKey = relevantPath[1];
      const mainMenu = menuList.find((m) => m.key === menuKey);
      if (components[menuKey]) {
        setActiveMainMenu(menuKey);
        setShowMainMenu(true);
        // setTitle(mainMenu?.label || "Meratractor - Dashboard");
        return;
      } else {
        const firstChild = mainMenu?.children?.[0];
        const firstChildKey = mainMenu?.children?.[0]?.key;
        if (firstChild && components[firstChildKey]) {
          setActiveMainMenu(menuKey);
          setShowMainMenu(true);
          // setTitle(firstChild.label || "Meratractor - Dashboard");
          history.replace(`${basePath}/${menuKey}/${firstChildKey}`);
          return;
        } else {
          const secondChildKey = firstChild?.children?.[0]?.key;
          setActiveMainMenu(menuKey);
          setShowMainMenu(true);
          setExpandedSubMenu((prev) => [...prev, firstChildKey]);
          // setTitle(firstChild.label || "Meratractor - Dashboard");
          history.replace(`${basePath}/${menuKey}/${secondChildKey}`);
          return;
        }
      }
    }
    if (relevantPath.length > 2) {
      const menuKey = relevantPath[1];
      const mainMenu = menuList.find((m) => m.key === menuKey);
      setActiveMainMenu(menuKey);
      setShowMainMenu(true);

      // setTitle(mainMenu?.label || "Meratractor - Dashboard");
      return;
    }
  }, [location.pathname, menuList, basePath]);

  // useEffect(() => {
  //   // Check if tour has been completed
  //   const tourCompleted = localStorage.getItem("sidebar_tour_completed");

  //   // Auto-start tour for first-time users after a delay to ensure DOM is ready
  //   if (!tourCompleted) {
  //     const timer = setTimeout(() => {
  //       console.log("Auto-starting sidebar tour");
  //       setRunTour(true);
  //     }, 2000);
  //     return () => clearTimeout(timer);
  //   }
  // }, []);

  // useEffect(() => {
  //   // Listen for tour start event
  //   const handleStartTour = () => {
  //     console.log("Manual tour start triggered");
  //     setRunTour(true);
  //   };

  //   window.addEventListener("startSidebarTour", handleStartTour);
  //   return () => {
  //     window.removeEventListener("startSidebarTour", handleStartTour);
  //   };
  // }, []);

  // const handleTourFinish = () => {
  //   console.log("Tour finished");
  //   setRunTour(false);
  // };

  // const handleTourOpenSidebar = () => {
  //   console.log("Tour requesting sidebar open");
  //   openSidebar();
  // };

  return (
    <>
      {/* <SidebarTour
        run={runTour}
        onFinish={handleTourFinish}
        onOpenSidebar={handleTourOpenSidebar}
      /> */}

      {/* <div style={{ height: "100%" }} className=" d-block d-lg-none">
        {isSidebarOpen && (
          <>
            <div
              class="offcanvas offcanvas-start show"
              data-bs-backdrop="static"
              tabindex="-1"
              id="staticBackdrop"
              aria-labelledby="staticBackdropLabel"
              style={{ zIndex: 1061, visibility: "visible" }}
            >
              <div class="offcanvas-header">
                <h5 class="offcanvas-title" id="staticBackdropLabel">
                  {dashboardTitle || "Dashboard"}
                </h5>
                <button
                  type="button"
                  class="btn-close"
                  data-bs-dismiss="offcanvas"
                  id="offcanvasCloseButton"
                  aria-label="Close"
                  onClick={closeSidebar}
                ></button>
              </div>
              <div
                class="offcanvas-body p-0"
                style={{ zIndex: 1065, height: "100%" }}
              >
                <div
                  className=" d-flex flex-column p-2"
                  style={{ height: "100%" }}
                >
                  <SideBarMenu />
                </div>
              </div>
            </div>
            <div
              class="offcanvas-backdrop fade show"
              style={{ zIndex: 1060 }}
              onClick={closeSidebar}
            ></div>
          </>
        )}

        <div
          className=" p-2 bg-glass rounded-2 border"
          style={{ overflowY: "auto", height: "100%" }}
        >
          {ActiveContentComponent ? (
            <>
              <Suspense fallback={<p className=" text-center">Loading...</p>}>
                <ActiveContentComponent />
              </Suspense>
            </>
          ) : (
            <p className="text-muted text-center">No content available</p>
          )}
        </div>
      </div> */}

      <div className=" d-lg-flex p-2 " style={{ height: "100%" }}>
        {/* Sidebar Area */}
        {showMobileSidebar ? (
          <>
            {isSidebarOpen && (
              <>
                <div
                  class="offcanvas offcanvas-start show"
                  data-bs-backdrop="static"
                  tabindex="-1"
                  id="staticBackdrop"
                  aria-labelledby="staticBackdropLabel"
                  style={{ zIndex: 1061, visibility: "visible" }}
                >
                  <div class="offcanvas-header">
                    <h5 class="offcanvas-title" id="staticBackdropLabel">
                      {dashboardTitle || "Dashboard"}
                    </h5>
                    <button
                      type="button"
                      class="btn-close"
                      data-bs-dismiss="offcanvas"
                      id="offcanvasCloseButton"
                      aria-label="Close"
                      onClick={closeSidebar}
                    ></button>
                  </div>
                  <div
                    class="offcanvas-body p-0"
                    style={{ zIndex: 1065, height: "100%" }}
                  >
                    <div
                      className=" d-flex flex-column p-2"
                      style={{ height: "100%" }}
                    >
                      <SideBarMenu />
                    </div>
                  </div>
                </div>
                <div
                  class="offcanvas-backdrop fade show"
                  style={{ zIndex: 1060 }}
                  onClick={closeSidebar}
                ></div>
              </>
            )}
          </>
        ) : (
          <SideBarMenu />
        )}

        {/* Content Area */}
        <div
          className="flex-grow-1 ms-lg-2 bg-glass rounded-2 border"
          style={{ overflowY: "auto", height: "100%" }}
        >
          {bannerComponent ? bannerComponent : null}
          {ActiveContentComponent ? (
            <div className=" bg-transparent p-2">
              <div>
                <Suspense fallback={<p className=" text-center">Loading...</p>}>
                  <ActiveContentComponent />
                </Suspense>
              </div>
            </div>
          ) : (
            <p className="text-muted text-center align-middle">
              Please select a page
            </p>
          )}
        </div>
      </div>
    </>
  );
}

export default SideBar;
